import React, { useState } from 'react';
import { Phone, Package, Search, CheckCircle, Gift, Truck, Calendar, Box, AlertCircle, Mail, Edit, X, Plus, Minus, MessageSquare, RefreshCw, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const api = axios.create({ baseURL: API_BASE });

// Contact info
const CONTACT_PHONE = '0750263064';
const CONTACT_EMAIL = 'pfatothalina@gmail.com';

// Format price
const formatPrice = (price) => {
    return new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(price || 0);
};

// Format date
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ro-RO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch {
        return '';
    }
};

// Removal reasons
const REMOVAL_REASONS = [
    { value: 'nu_mai_doresc', label: 'Nu îl mai doresc' },
    { value: 'in_plus', label: 'Este în plus' },
    { value: 'nu_e_comanda_mea', label: 'Nu e comanda mea' },
    { value: 'altele', label: 'Altele' }
];

export default function ClientPortal() {
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [requestingDelivery, setRequestingDelivery] = useState({});
    
    // Promo offers state
    const [promoOffers, setPromoOffers] = useState([]);
    const [showPromoModal, setShowPromoModal] = useState(false);
    const [promoCart, setPromoCart] = useState([]);
    const [promoOrderForm, setPromoOrderForm] = useState({ name: '', phone: '', details: '' });
    const [submittingPromoOrder, setSubmittingPromoOrder] = useState(false);
    const [reservedProducts, setReservedProducts] = useState({}); // Track what user already reserved
    const [expandedDescriptions, setExpandedDescriptions] = useState({}); // Track expanded descriptions
    const [expandedImage, setExpandedImage] = useState(null); // Track expanded image
    
    // Toggle description expand/collapse
    const toggleDescription = (productId) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };
    
    // Get client fingerprint for reservation limiting
    const getClientFingerprint = () => {
        let fp = localStorage.getItem('promo_fingerprint');
        if (!fp) {
            fp = 'fp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('promo_fingerprint', fp);
        }
        return fp;
    };
    
    // Load promo offers on mount
    React.useEffect(() => {
        loadPromoOffers();
        // Load reserved products from localStorage
        const saved = localStorage.getItem('reserved_promo_products');
        if (saved) {
            try {
                setReservedProducts(JSON.parse(saved));
            } catch (e) {
                // Ignore parse errors
            }
        }
    }, []);
    
    const loadPromoOffers = async () => {
        try {
            const res = await api.get('/api/web/offers');
            const offersData = Array.isArray(res.data) ? res.data : (res.data?.offers || []);
            const activeOffers = offersData.filter(o => o.active && o.products?.length > 0);
            setPromoOffers(activeOffers);
        } catch (err) {
            console.error('Error loading promo offers:', err);
        }
    };
    
    const isProductInCart = (productId) => {
        return promoCart.some(p => p.id === productId);
    };
    
    const hasReservedProduct = (productId, phoneNumber) => {
        // Check localStorage (cookie-like)
        if (reservedProducts[productId]) return true;
        // Phone will be checked server-side
        return false;
    };
    
    const getAvailableStock = (product) => {
        const stock = product.stock || 0;
        const reserved = product.reservedCount || 0;
        return Math.max(0, stock - reserved);
    };
    
    const addToPromoCart = (product, isWishlist = false) => {
        // Check if already in cart
        if (isProductInCart(product.id)) {
            toast.info('Produsul este deja în coș');
            return;
        }
        
        // Check if already reserved this product (localStorage)
        if (reservedProducts[product.id]) {
            toast.error('Ați comandat deja acest produs');
            return;
        }
        
        setPromoCart(prev => [...prev, { 
            ...product, 
            quantity: 1, 
            isWishlist: isWishlist 
        }]);
        
        if (isWishlist) {
            toast.success(`${product.name} adăugat ca dorință`);
        } else {
            toast.success(`${product.name} adăugat în coș`);
        }
    };
    
    const removeFromPromoCart = (productId) => {
        setPromoCart(prev => prev.filter(p => p.id !== productId));
    };
    
    // No quantity changes - max 1 per product
    const updatePromoCartQty = (productId, delta) => {
        // Disabled - each product limited to qty 1
        toast.info('Limită: 1 bucată per produs');
    };
    
    const submitPromoOrder = async () => {
        if (!promoOrderForm.name.trim()) {
            toast.error('Numele este obligatoriu');
            return;
        }
        if (!promoOrderForm.phone.trim()) {
            toast.error('Telefonul este obligatoriu');
            return;
        }
        if (promoCart.length === 0) {
            toast.error('Adăugați cel puțin un produs');
            return;
        }
        
        setSubmittingPromoOrder(true);
        try {
            const fingerprint = getClientFingerprint();
            
            await api.post('/api/public/promo-order', {
                name: promoOrderForm.name.trim(),
                phone: promoOrderForm.phone.trim(),
                details: promoOrderForm.details.trim(),
                products: promoCart.map(p => ({
                    id: p.id,
                    name: p.name,
                    promoPrice: p.promoPrice,
                    basePrice: p.basePrice || p.price,
                    isPackage: p.isPackage,
                    quantity: 1, // Always 1
                    isWishlist: p.isWishlist || false
                })),
                fingerprint: fingerprint
            });
            
            // Mark products as reserved in localStorage
            const newReserved = { ...reservedProducts };
            promoCart.forEach(p => {
                newReserved[p.id] = {
                    phone: promoOrderForm.phone.trim(),
                    timestamp: Date.now()
                };
            });
            setReservedProducts(newReserved);
            localStorage.setItem('reserved_promo_products', JSON.stringify(newReserved));
            
            toast.success('Comanda a fost trimisă! Vă vom contacta în curând.');
            setShowPromoModal(false);
            setPromoCart([]);
            setPromoOrderForm({ name: '', phone: '', details: '' });
            
            // Reload offers to get updated stock
            loadPromoOffers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la trimiterea comenzii');
        } finally {
            setSubmittingPromoOrder(false);
        }
    };
    
    const getPromoCartTotal = () => {
        return promoCart.reduce((sum, p) => sum + (p.promoPrice || 0) * p.quantity, 0);
    };
    
    // Modification modal state
    const [showModifyModal, setShowModifyModal] = useState(false);
    const [modifyingCustomer, setModifyingCustomer] = useState(null);
    const [modifications, setModifications] = useState([]);
    const [addProductName, setAddProductName] = useState('');
    const [addProductQty, setAddProductQty] = useState(1);
    const [submittingModification, setSubmittingModification] = useState(false);
    const [resettingModification, setResettingModification] = useState({});

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!phone.trim()) {
            setError('Introduceți numărul de telefon');
            return;
        }

        setLoading(true);
        setError('');
        setData(null);

        try {
            const res = await api.post('/api/public/customer-lookup', { phone: phone.trim() });
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Nu am găsit comenzi pentru acest număr');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestDelivery = async (customerId, hasReservations) => {
        setRequestingDelivery(prev => ({ ...prev, [customerId]: true }));
        try {
            await api.post('/api/public/request-delivery', { customer_id: customerId });
            const message = hasReservations 
                ? 'Cererea de pregătire a fost înregistrată! Vă vom contacta în curând.'
                : 'Cererea a fost înregistrată! Vă vom contacta în curând.';
            toast.success(message);
            setData(prev => ({
                ...prev,
                customers: prev.customers.map(c => 
                    c.customer_id === customerId 
                        ? { ...c, has_pending_request: true }
                        : c
                )
            }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la trimiterea cererii');
        } finally {
            setRequestingDelivery(prev => ({ ...prev, [customerId]: false }));
        }
    };

    const openModifyModal = (customer) => {
        setModifyingCustomer(customer);
        setModifications([]);
        setAddProductName('');
        setAddProductQty(1);
        setShowModifyModal(true);
    };

    const handleResetModification = async (customerId) => {
        setResettingModification(prev => ({ ...prev, [customerId]: true }));
        try {
            await api.post('/api/public/reset-modification-request', { customer_id: customerId });
            toast.success('Cererea a fost resetată. Puteți face o nouă cerere.');
            setData(prev => ({
                ...prev,
                customers: prev.customers.map(c => 
                    c.customer_id === customerId 
                        ? { ...c, has_modification_request: false }
                        : c
                )
            }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la resetarea cererii');
        } finally {
            setResettingModification(prev => ({ ...prev, [customerId]: false }));
        }
    };

    const addRemovalItem = (item, source) => {
        // Check if already in modifications
        const existing = modifications.find(m => m.item_name === item.name && m.action === 'remove');
        if (existing) {
            toast.info('Acest produs este deja în lista de modificări');
            return;
        }
        
        setModifications(prev => [...prev, {
            item_name: item.name,
            barcode: item.barcode || '',
            action: 'remove',
            reason: '',
            custom_reason: '',
            quantity: item.quantity,
            source: source
        }]);
    };

    const updateModificationReason = (index, reason) => {
        setModifications(prev => prev.map((m, i) => 
            i === index ? { ...m, reason, custom_reason: reason !== 'altele' ? '' : m.custom_reason } : m
        ));
    };

    const updateCustomReason = (index, customReason) => {
        setModifications(prev => prev.map((m, i) => 
            i === index ? { ...m, custom_reason: customReason } : m
        ));
    };

    const removeModification = (index) => {
        setModifications(prev => prev.filter((_, i) => i !== index));
    };

    const addNewProduct = () => {
        if (!addProductName.trim()) {
            toast.error('Introduceți numele produsului');
            return;
        }
        
        setModifications(prev => [...prev, {
            item_name: addProductName.trim(),
            action: 'add',
            reason: '',
            custom_reason: '',
            quantity: addProductQty
        }]);
        setAddProductName('');
        setAddProductQty(1);
    };

    const submitModifications = async () => {
        if (modifications.length === 0) {
            toast.error('Adăugați cel puțin o modificare');
            return;
        }

        // Validate all removal items have reasons
        const invalidRemovals = modifications.filter(m => m.action === 'remove' && !m.reason);
        if (invalidRemovals.length > 0) {
            toast.error('Selectați motivul pentru fiecare produs eliminat');
            return;
        }

        // Validate "altele" reasons have custom text
        const invalidCustom = modifications.filter(m => m.reason === 'altele' && !m.custom_reason.trim());
        if (invalidCustom.length > 0) {
            toast.error('Completați motivul pentru opțiunea "Altele"');
            return;
        }

        setSubmittingModification(true);
        try {
            await api.post('/api/public/modification-request', {
                customer_id: modifyingCustomer.base_customer_id || modifyingCustomer.customer_id,
                items: modifications.map(m => ({
                    item_name: m.item_name,
                    barcode: m.barcode || '',
                    action: m.action,
                    reason: m.reason,
                    custom_reason: m.custom_reason,
                    quantity: m.quantity
                })),
                notes: ''
            });
            toast.success('Cererea de modificare a fost trimisă! Vă vom contacta pentru confirmare.');
            setShowModifyModal(false);
            setModifications([]);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la trimiterea cererii');
        } finally {
            setSubmittingModification(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
            <Toaster position="top-center" richColors />
            
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-teal-100">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <Package className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-zinc-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    TOTH ALINA COMMERCE PFA
                                </h1>
                                <p className="text-xs text-zinc-500">Verifică comenzile tale</p>
                            </div>
                        </div>
                        {/* Contact Info */}
                        <div className="hidden sm:flex items-center gap-4 text-sm">
                            <a href={`tel:${CONTACT_PHONE}`} className="flex items-center gap-1.5 text-zinc-600 hover:text-teal-600 transition-colors">
                                <Phone size={14} />
                                {CONTACT_PHONE}
                            </a>
                            <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-1.5 text-zinc-600 hover:text-teal-600 transition-colors">
                                <Mail size={14} />
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                    </div>
                    {/* Mobile contact */}
                    <div className="flex sm:hidden items-center justify-center gap-4 mt-3 pt-3 border-t border-zinc-100 text-sm">
                        <a href={`tel:${CONTACT_PHONE}`} className="flex items-center gap-1.5 text-teal-600">
                            <Phone size={14} />
                            {CONTACT_PHONE}
                        </a>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-1.5 text-teal-600">
                            <Mail size={14} />
                            Email
                        </a>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 py-8">
                
                {/* Search Form - Verifică comanda */}
                <div className="bg-white rounded-2xl shadow-lg border border-teal-100 p-6 mb-8">
                    <h2 className="text-lg font-semibold text-zinc-800 mb-4 flex items-center gap-2">
                        <Phone className="w-5 h-5 text-teal-600" />
                        Verifică comanda
                    </h2>
                    <form onSubmit={handleSearch} className="flex gap-3">
                        <div className="flex-1 relative">
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Introduceți numărul de telefon (ex: 0712345678)"
                                className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-lg"
                                data-testid="phone-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-medium hover:from-teal-600 hover:to-emerald-700 transition-all disabled:opacity-50 flex items-center gap-2"
                            data-testid="search-button"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Search className="w-5 h-5" />
                            )}
                            Caută
                        </button>
                    </form>
                    
                    {error && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            {error}
                        </div>
                    )}
                </div>
                
                {/* PROMOTIONS SECTION - Only show if there are active offers */}
                {promoOffers.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-zinc-800 flex items-center gap-2">
                                <Gift className="w-6 h-6 text-red-500" />
                                Oferte Speciale
                            </h2>
                            {promoCart.length > 0 && (
                                <button
                                    onClick={() => setShowPromoModal(true)}
                                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-medium hover:from-red-600 hover:to-pink-700 transition-all flex items-center gap-2 animate-pulse"
                                >
                                    <Package size={18} />
                                    Coș ({promoCart.length}) - {formatPrice(getPromoCartTotal())} RON
                                </button>
                            )}
                        </div>
                        
                        {/* Offers Grid - 2x2 layout on desktop, 1 column on mobile */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {promoOffers.slice(0, 4).map((offer, offerIndex) => (
                                <div 
                                    key={offer.id}
                                    className="bg-white rounded-2xl shadow-lg border border-red-100 overflow-hidden"
                                >
                                    {/* Offer Header */}
                                    <div className="bg-gradient-to-r from-red-500 to-pink-600 px-4 py-2 text-white font-semibold flex items-center gap-2">
                                        <Gift size={18} />
                                        {offer.name || `Ofertă ${offerIndex + 1}`}
                                    </div>
                                    
                                    {/* Products in this offer */}
                                    <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
                                        {offer.products.map((product) => {
                                            const availableStock = getAvailableStock(product);
                                            const isOutOfStock = availableStock <= 0;
                                            const alreadyReserved = reservedProducts[product.id];
                                            const inCart = isProductInCart(product.id);
                                            const discount = product.discount || (product.basePrice && product.promoPrice ? 
                                                Math.round(((product.basePrice - product.promoPrice) / product.basePrice) * 100) : 0);
                                            
                                            return (
                                                <div 
                                                    key={product.id}
                                                    className={`relative flex items-center gap-3 p-3 rounded-xl transition-all ${
                                                        alreadyReserved ? 'bg-green-50 border border-green-200' :
                                                        inCart ? 'bg-amber-50 border border-amber-200' :
                                                        isOutOfStock ? 'bg-zinc-50 border border-zinc-200' :
                                                        'bg-zinc-50 hover:bg-zinc-100 border border-transparent'
                                                    }`}
                                                >
                                                    {/* Discount Badge */}
                                                    {discount > 0 && (
                                                        <div className="absolute -top-1 -left-1 bg-red-600 text-white px-2 py-0.5 rounded-br-lg rounded-tl-lg text-xs font-bold shadow-md">
                                                            -{discount}%
                                                        </div>
                                                    )}
                                                    
                                                    {/* Package Badge */}
                                                    {product.isPackage && (
                                                        <div className="absolute -top-1 right-2 bg-purple-600 text-white px-2 py-0.5 rounded-b-lg text-xs font-bold">
                                                            PACHET
                                                        </div>
                                                    )}
                                                    
                                                    {/* Product Image - Clickable to expand */}
                                                    {product.imageUrl ? (
                                                        <img 
                                                            src={product.imageUrl} 
                                                            alt={product.name}
                                                            onClick={() => setExpandedImage({ url: product.imageUrl, name: product.name })}
                                                            className={`w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity ${isOutOfStock ? 'opacity-50' : ''}`}
                                                        />
                                                    ) : (
                                                        <div className={`w-16 h-16 bg-zinc-200 rounded-lg flex items-center justify-center ${isOutOfStock ? 'opacity-50' : ''}`}>
                                                            <Package size={24} className="text-zinc-400" />
                                                        </div>
                                                    )}
                                                    
                                                    {/* Product Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`font-medium text-zinc-800 text-sm ${isOutOfStock ? 'opacity-70' : ''}`}>
                                                            {product.name}
                                                        </p>
                                                        {product.description && (
                                                            <div className="mt-0.5">
                                                                <p className={`text-xs text-zinc-500 ${expandedDescriptions[product.id] ? '' : 'line-clamp-1'}`}>
                                                                    {product.description}
                                                                </p>
                                                                {product.description.length > 40 && (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); toggleDescription(product.id); }}
                                                                        className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-0.5 mt-0.5"
                                                                    >
                                                                        {expandedDescriptions[product.id] ? (
                                                                            <>Mai puțin <ChevronUp size={12} /></>
                                                                        ) : (
                                                                            <>Mai mult <ChevronDown size={12} /></>
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Price with strikethrough */}
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {(product.basePrice || product.price) > 0 && (product.basePrice || product.price) !== product.promoPrice && (
                                                                <span className="text-xs line-through text-zinc-400">
                                                                    {formatPrice(product.basePrice || product.price)} RON
                                                                </span>
                                                            )}
                                                            <span className="font-bold text-red-600 text-lg">
                                                                {formatPrice(product.promoPrice)} RON
                                                            </span>
                                                            {product.isPackage && (
                                                                <span className="text-xs text-purple-600">/pachet</span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Stock indicator */}
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {isOutOfStock ? (
                                                                <span className="text-xs text-orange-600 font-medium">Stoc epuizat - dar poți comanda!</span>
                                                            ) : (
                                                                <span className="text-xs text-emerald-600">
                                                                    Disponibil: {availableStock} {availableStock === 1 ? 'bucată' : 'bucăți'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Action Button */}
                                                    <div className="flex flex-col items-center gap-1">
                                                        {alreadyReserved ? (
                                                            <div className="p-2 bg-green-100 rounded-lg text-green-600">
                                                                <CheckCircle size={20} />
                                                            </div>
                                                        ) : inCart ? (
                                                            <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                                                                <Package size={20} />
                                                            </div>
                                                        ) : isOutOfStock ? (
                                                            <button
                                                                onClick={() => addToPromoCart(product, true)}
                                                                className="px-3 py-2 bg-orange-100 hover:bg-orange-200 rounded-lg text-orange-600 transition-colors text-xs font-medium"
                                                                title="Adaugă ca dorință"
                                                            >
                                                                Doresc
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => addToPromoCart(product, false)}
                                                                className="p-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-600 transition-colors"
                                                                title="Adaugă în coș"
                                                            >
                                                                <Plus size={20} />
                                                            </button>
                                                        )}
                                                        {alreadyReserved && (
                                                            <span className="text-xs text-green-600">Comandat</span>
                                                        )}
                                                        {inCart && !alreadyReserved && (
                                                            <span className="text-xs text-amber-600">În coș</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Order Button if cart has items */}
                        {promoCart.length > 0 && (
                            <div className="mt-4 text-center">
                                <button
                                    onClick={() => setShowPromoModal(true)}
                                    className="px-8 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-red-600 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
                                >
                                    Comandă Promoții ({promoCart.length} produse) - {formatPrice(getPromoCartTotal())} RON
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Results */}
                {data && data.customers && data.customers.length > 0 && (
                    <div className="space-y-6">
                        <div className="text-center text-zinc-600 mb-4">
                            Rezultate pentru: <span className="font-semibold text-teal-600">{data.phone}</span>
                        </div>

                        {data.customers.map((customer) => {
                            // New grouped structure
                            const hasSubCustomers = customer.sub_customers && customer.sub_customers.length > 0;
                            const subCustomers = customer.sub_customers || [customer]; // fallback for old structure
                            
                            // Calculate if has any reservations or orders across all sub-customers
                            const allReservationItems = subCustomers.flatMap(sc => sc.reservations?.items || []);
                            const allReservationGifts = subCustomers.flatMap(sc => sc.reservations?.gifts || []);
                            const allOrderItems = subCustomers.flatMap(sc => sc.orders?.items || []);
                            const allOrderGifts = subCustomers.flatMap(sc => sc.orders?.gifts || []);
                            const totalOrdersTransportFee = subCustomers.reduce((sum, sc) => sum + (sc.orders?.transport_fee || 0), 0);
                            const totalReservationsTransportFee = subCustomers.reduce((sum, sc) => sum + (sc.reservations?.transport_fee || 0), 0);
                            const totalTransportFee = totalOrdersTransportFee + totalReservationsTransportFee;
                            
                            const hasReservations = allReservationItems.length > 0 || allReservationGifts.length > 0;
                            const hasOrders = allOrderItems.length > 0 || allOrderGifts.length > 0;
                            
                            // Use combined totals from grouped structure, or fallback
                            const combinedGrandTotal = customer.combined_grand_total || customer.grand_total || 0;
                            const combinedReservationsTotal = customer.combined_reservations_total || customer.reservations?.total || 0;
                            const combinedOrdersTotal = customer.combined_orders_total || customer.orders?.total || 0;
                            
                            // Get the base customer ID for display
                            const displayId = customer.base_customer_id || customer.customer_id;
                            
                            return (
                            <div 
                                key={displayId}
                                className="bg-white rounded-2xl shadow-lg border border-teal-100 overflow-hidden"
                                data-testid={`customer-card-${displayId}`}
                            >
                                {/* Customer Header - Combined Total */}
                                <div className="bg-gradient-to-r from-teal-500 to-emerald-600 px-6 py-4 text-white">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Box className="w-6 h-6" />
                                            <div>
                                                <h3 className="font-bold text-lg">
                                                    {customer.name || `Comanda #${displayId}`}
                                                </h3>
                                                {customer.address && (
                                                    <p className="text-teal-100 text-sm flex items-center gap-1">
                                                        <MapPin size={12} />
                                                        {customer.address}
                                                    </p>
                                                )}
                                                {subCustomers.length > 1 && (
                                                    <p className="text-teal-200 text-xs mt-1">
                                                        {subCustomers.length} cutii/liste
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-bold">{formatPrice(combinedGrandTotal)} RON</p>
                                            <p className="text-teal-100 text-sm">Total de plată</p>
                                        </div>
                                    </div>
                                    
                                    {/* Subtotals breakdown */}
                                    <div className="mt-3 pt-3 border-t border-teal-400/30 flex justify-end gap-6 text-sm">
                                        {combinedReservationsTotal > 0 && (
                                            <div className="text-teal-100">
                                                <span className="opacity-75">Rezervări:</span> <span className="font-semibold text-white">{formatPrice(combinedReservationsTotal)} RON</span>
                                            </div>
                                        )}
                                        {combinedOrdersTotal > 0 && (
                                            <div className="text-teal-100">
                                                <span className="opacity-75">Comenzi:</span> <span className="font-semibold text-white">{formatPrice(combinedOrdersTotal)} RON</span>
                                            </div>
                                        )}
                                        {totalTransportFee > 0 && (
                                            <div className="text-teal-100">
                                                <span className="opacity-75">Transport inclus:</span> <span className="font-semibold text-white">{formatPrice(totalTransportFee)} RON</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="p-6">
                                    {/* Show each sub-customer's details */}
                                    {subCustomers.map((subCustomer, subIdx) => {
                                        const scHasReservations = (subCustomer.reservations?.items?.length || 0) > 0 || (subCustomer.reservations?.gifts?.length || 0) > 0;
                                        const scHasOrders = (subCustomer.orders?.items?.length || 0) > 0 || (subCustomer.orders?.gifts?.length || 0) > 0;
                                        const boxLabel = subCustomer.box_number ? `Cutia ${subCustomer.box_number}` : (subCustomer.customer_id !== displayId ? subCustomer.customer_id : null);
                                        
                                        return (
                                            <div key={subCustomer.customer_id} className={subIdx > 0 ? "mt-6 pt-6 border-t border-zinc-200" : ""}>
                                                {/* Sub-customer header (only if multiple) */}
                                                {subCustomers.length > 1 && (
                                                    <div className="flex items-center justify-between mb-4 pb-2 border-b border-zinc-100">
                                                        <span className="font-semibold text-zinc-700 flex items-center gap-2">
                                                            <Package size={16} className="text-teal-500" />
                                                            {boxLabel || `Lista ${subIdx + 1}`}
                                                        </span>
                                                        <span className="text-teal-600 font-bold">
                                                            {formatPrice(subCustomer.grand_total)} RON
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {/* Reservations Section */}
                                                {scHasReservations && (
                                                    <div className="mb-4">
                                                        <h4 className="font-semibold text-amber-600 mb-3 flex items-center gap-2">
                                                            <Package className="w-4 h-4" />
                                                            Rezervări
                                                            {subCustomers.length > 1 && <span className="text-xs text-zinc-400">({boxLabel || `Lista ${subIdx + 1}`})</span>}
                                                        </h4>
                                                        <div className="bg-amber-50 rounded-xl p-4">
                                                            {/* Regular items */}
                                                            <div className="space-y-2">
                                                                {(subCustomer.reservations?.items || []).map((item, idx) => (
                                                                    <div key={idx} className="flex justify-between items-center py-1 border-b border-amber-100 last:border-0">
                                                                        <div className="flex-1">
                                                                            <span className="text-zinc-400 mr-2">{idx + 1}.</span>
                                                                            <span className="text-zinc-800">{item.name}</span>
                                                                            <span className="text-zinc-500 ml-2">x{item.quantity}</span>
                                                                        </div>
                                                                        <span className="font-medium text-amber-700">{formatPrice(item.total)} RON</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            
                                                            {/* Gifts */}
                                                            {(subCustomer.reservations?.gifts?.length || 0) > 0 && (
                                                                <div className="mt-3 pt-3 border-t border-amber-200">
                                                                    <p className="text-pink-600 font-medium mb-1 flex items-center gap-1">
                                                                        <Gift className="w-4 h-4" /> CADOU
                                                                    </p>
                                                                    {subCustomer.reservations.gifts.map((gift, idx) => (
                                                                        <p key={idx} className="text-pink-700 text-sm ml-5">
                                                                            {gift.name} {gift.quantity > 1 && `x${gift.quantity}`}
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Transport fee for reservations */}
                                                            {(subCustomer.reservations?.transport_fee || 0) > 0 && (
                                                                <div className="mt-3 pt-3 border-t border-amber-200 flex justify-between items-center">
                                                                    <span className="text-zinc-600 flex items-center gap-1">
                                                                        <Truck className="w-4 h-4" /> Transport
                                                                    </span>
                                                                    <span className="font-medium text-amber-700">{formatPrice(subCustomer.reservations.transport_fee)} RON</span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Subtotal */}
                                                            <div className="mt-3 pt-3 border-t border-amber-200 flex justify-between font-semibold">
                                                                <span className="text-amber-800">Subtotal Rezervări</span>
                                                                <span className="text-amber-700">{formatPrice(subCustomer.reservations?.total || 0)} RON</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Orders Section */}
                                                {scHasOrders && (
                                                    <div className="mb-4">
                                                        <h4 className="font-semibold text-emerald-600 mb-3 flex items-center gap-2">
                                                            <CheckCircle className="w-4 h-4" />
                                                            Comandă
                                                            {subCustomers.length > 1 && <span className="text-xs text-zinc-400">({boxLabel || `Lista ${subIdx + 1}`})</span>}
                                                        </h4>
                                                        <div className="bg-emerald-50 rounded-xl p-4">
                                                            {/* Regular items */}
                                                            <div className="space-y-2">
                                                                {(subCustomer.orders?.items || []).map((item, idx) => (
                                                                    <div key={idx} className="flex justify-between items-center py-1 border-b border-emerald-100 last:border-0">
                                                                        <div className="flex-1">
                                                                            <span className="text-zinc-400 mr-2">{idx + 1}.</span>
                                                                            <span className="text-zinc-800">{item.name}</span>
                                                                            <span className="text-zinc-500 ml-2">x{item.quantity}</span>
                                                                        </div>
                                                                        <span className="font-medium text-emerald-700">{formatPrice(item.total)} RON</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            
                                                            {/* Gifts */}
                                                            {(subCustomer.orders?.gifts?.length || 0) > 0 && (
                                                                <div className="mt-3 pt-3 border-t border-emerald-200">
                                                                    <p className="text-pink-600 font-medium mb-1 flex items-center gap-1">
                                                                        <Gift className="w-4 h-4" /> CADOU
                                                                    </p>
                                                                    {subCustomer.orders.gifts.map((gift, idx) => (
                                                                        <p key={idx} className="text-pink-700 text-sm ml-5">
                                                                            {gift.name} {gift.quantity > 1 && `x${gift.quantity}`}
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Transport fee */}
                                                            {(subCustomer.orders?.transport_fee || 0) > 0 && (
                                                                <div className="mt-3 pt-3 border-t border-emerald-200 flex justify-between items-center">
                                                                    <span className="text-zinc-600 flex items-center gap-1">
                                                                        <Truck className="w-4 h-4" /> Transport
                                                                    </span>
                                                                    <span className="font-medium text-emerald-700">{formatPrice(subCustomer.orders.transport_fee)} RON</span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Subtotal */}
                                                            <div className="mt-3 pt-3 border-t border-emerald-200 flex justify-between font-semibold">
                                                                <span className="text-emerald-800">Subtotal Comandă</span>
                                                                <span className="text-emerald-700">{formatPrice(subCustomer.orders?.total || 0)} RON</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Date info */}
                                    {customer.oldest_date && (
                                        <div className="flex items-center gap-2 text-zinc-500 text-sm mb-4">
                                            <Calendar className="w-4 h-4" />
                                            Prima rezervare: {formatDate(customer.oldest_date)}
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="border-t border-zinc-100 pt-4 space-y-3">
                                        {/* Show modification request status */}
                                        {customer.has_modification_request && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <MessageSquare className="w-6 h-6 text-amber-600" />
                                                        <div>
                                                            <p className="font-medium text-amber-800">Cerere de modificare trimisă</p>
                                                            <p className="text-sm text-amber-600">Așteptați să vă contactăm pentru confirmare.</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleResetModification(displayId)}
                                                        disabled={resettingModification[displayId]}
                                                        className="px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium flex items-center gap-1 transition-all disabled:opacity-50"
                                                        data-testid={`reset-modification-${displayId}`}
                                                    >
                                                        {resettingModification[displayId] ? (
                                                            <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="w-4 h-4" />
                                                        )}
                                                        Refă cererea
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {customer.has_pending_request ? (
                                            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                                                <CheckCircle className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                                                <p className="font-medium text-teal-800">Cerere înregistrată</p>
                                                <p className="text-sm text-teal-600">Vă vom contacta în curând pentru livrare/ridicare.</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Request Delivery/Preparation Button */}
                                                <button
                                                    onClick={() => handleRequestDelivery(displayId, hasReservations)}
                                                    disabled={requestingDelivery[displayId]}
                                                    className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                                    data-testid={`request-delivery-${displayId}`}
                                                >
                                                    {requestingDelivery[displayId] ? (
                                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <Truck className="w-5 h-5" />
                                                    )}
                                                    {hasReservations ? 'Solicit pregătirea' : 'Solicit comanda'}
                                                </button>
                                                
                                                {/* Modify Order Button - only show if no modification request pending */}
                                                {!customer.has_modification_request && (
                                                    <button
                                                        onClick={() => openModifyModal(customer)}
                                                        className="w-full py-3 bg-white border-2 border-amber-400 text-amber-700 rounded-xl font-medium hover:bg-amber-50 transition-all flex items-center justify-center gap-2"
                                                        data-testid={`modify-order-${displayId}`}
                                                    >
                                                        <Edit className="w-5 h-5" />
                                                        Modifică comanda
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}

                {/* Empty state when no search yet */}
                {!data && !error && !loading && (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Package className="w-10 h-10 text-teal-600" />
                        </div>
                        <h3 className="text-lg font-medium text-zinc-700 mb-2">
                            Verifică statusul comenzii tale
                        </h3>
                        <p className="text-zinc-500 max-w-md mx-auto">
                            Introduceți numărul de telefon cu care ați plasat comanda pentru a vedea produsele rezervate și totalul de plată.
                        </p>
                    </div>
                )}
            </main>
            
            {/* Image Lightbox Modal */}
            {expandedImage && (
                <div 
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setExpandedImage(null)}
                >
                    <div className="relative max-w-3xl max-h-[90vh] w-full">
                        <button
                            onClick={() => setExpandedImage(null)}
                            className="absolute -top-10 right-0 text-white hover:text-zinc-300 transition-colors"
                        >
                            <X size={32} />
                        </button>
                        <img 
                            src={expandedImage.url} 
                            alt={expandedImage.name}
                            className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <p className="text-white text-center mt-3 font-medium">{expandedImage.name}</p>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer className="bg-white border-t border-zinc-100 mt-auto">
                <div className="max-w-4xl mx-auto px-4 py-6 text-center">
                    <p className="text-zinc-700 font-medium">TOTH ALINA COMMERCE PFA</p>
                    <div className="flex items-center justify-center gap-4 mt-2 text-sm text-zinc-500">
                        <a href={`tel:${CONTACT_PHONE}`} className="flex items-center gap-1 hover:text-teal-600">
                            <Phone size={14} />
                            {CONTACT_PHONE}
                        </a>
                        <span>•</span>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-1 hover:text-teal-600">
                            <Mail size={14} />
                            {CONTACT_EMAIL}
                        </a>
                    </div>
                    <p className="text-xs text-zinc-400 mt-2">CIF: 54080688 • Reg.com: F2026010134009</p>
                </div>
            </footer>

            {/* Promo Order Modal */}
            {showPromoModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden my-4">
                        <div className="bg-gradient-to-r from-red-500 to-pink-600 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-white font-bold text-lg flex items-center gap-2">
                                <Gift size={20} />
                                Comandă Promoții
                            </h2>
                            <button
                                onClick={() => setShowPromoModal(false)}
                                className="text-white/80 hover:text-white p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            {/* Cart Items */}
                            {promoCart.length > 0 ? (
                                <div className="space-y-3 mb-6">
                                    {promoCart.map((item) => (
                                        <div 
                                            key={item.id}
                                            className={`flex items-center gap-3 p-3 rounded-xl ${
                                                item.isWishlist ? 'bg-orange-50 border border-orange-200' : 'bg-zinc-50'
                                            }`}
                                        >
                                            {item.imageUrl ? (
                                                <img 
                                                    src={item.imageUrl} 
                                                    alt={item.name}
                                                    className="w-12 h-12 object-cover rounded-lg"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                                                    <Package size={18} className="text-zinc-400" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium text-zinc-800 text-sm truncate">{item.name}</p>
                                                    {item.isWishlist && (
                                                        <span className="px-1.5 py-0.5 bg-orange-200 text-orange-700 text-xs rounded">Dorință</span>
                                                    )}
                                                    {item.isPackage && (
                                                        <span className="px-1.5 py-0.5 bg-purple-200 text-purple-700 text-xs rounded">Pachet</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {(item.basePrice || item.price) > 0 && (item.basePrice || item.price) !== item.promoPrice && (
                                                        <span className="text-xs line-through text-zinc-400">{formatPrice(item.basePrice || item.price)}</span>
                                                    )}
                                                    <span className="text-red-600 font-semibold text-sm">{formatPrice(item.promoPrice)} RON</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-zinc-600">×1</span>
                                                <button
                                                    onClick={() => removeFromPromoCart(item.id)}
                                                    className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Info about limit */}
                                    <p className="text-xs text-zinc-500 text-center">
                                        Limită: 1 bucată din fiecare produs per comandă
                                    </p>
                                    
                                    {/* Total */}
                                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-xl border border-red-200">
                                        <span className="font-semibold text-zinc-700">Total:</span>
                                        <span className="text-xl font-bold text-red-600">{formatPrice(getPromoCartTotal())} RON</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6 text-zinc-500">
                                    <Package className="w-12 h-12 mx-auto mb-2 text-zinc-300" />
                                    <p>Coșul este gol</p>
                                </div>
                            )}
                            
                            {/* Contact Form */}
                            {promoCart.length > 0 && (
                                <div className="space-y-4 border-t border-zinc-200 pt-6">
                                    <h3 className="font-semibold text-zinc-700">Date de Contact</h3>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-zinc-600 mb-1">
                                            Nume complet *
                                        </label>
                                        <input
                                            type="text"
                                            value={promoOrderForm.name}
                                            onChange={(e) => setPromoOrderForm(p => ({ ...p, name: e.target.value }))}
                                            placeholder="Introduceți numele"
                                            className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                                            data-testid="promo-order-name"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-zinc-600 mb-1">
                                            Telefon *
                                        </label>
                                        <input
                                            type="tel"
                                            value={promoOrderForm.phone}
                                            onChange={(e) => setPromoOrderForm(p => ({ ...p, phone: e.target.value }))}
                                            placeholder="Ex: 0712345678"
                                            className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                                            data-testid="promo-order-phone"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-zinc-600 mb-1">
                                            Detalii suplimentare (opțional)
                                        </label>
                                        <textarea
                                            value={promoOrderForm.details}
                                            onChange={(e) => setPromoOrderForm(p => ({ ...p, details: e.target.value }))}
                                            placeholder="Adresă de livrare, preferințe, etc."
                                            rows={2}
                                            className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                                            data-testid="promo-order-details"
                                        />
                                    </div>
                                    
                                    {/* Submit Button */}
                                    <button
                                        onClick={submitPromoOrder}
                                        disabled={submittingPromoOrder || !promoOrderForm.name.trim() || !promoOrderForm.phone.trim()}
                                        className="w-full py-4 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-red-600 hover:to-pink-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                        data-testid="submit-promo-order"
                                    >
                                        {submittingPromoOrder ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <Truck className="w-5 h-5" />
                                                Trimite Comanda
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modification Modal */}
            {showModifyModal && modifyingCustomer && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden my-4">
                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-white font-bold text-lg flex items-center gap-2">
                                <Edit size={20} />
                                Modifică Comanda
                            </h2>
                            <button
                                onClick={() => setShowModifyModal(false)}
                                className="text-white/80 hover:text-white p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            {/* Instructions */}
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                                <p className="text-amber-800 text-sm">
                                    <strong>Cum funcționează:</strong> Selectați produsele pe care doriți să le eliminați sau adăugați produse noi. După trimitere, vă vom contacta pentru confirmare.
                                </p>
                            </div>

                            {/* Get all items from all sub_customers */}
                            {(() => {
                                const subCustomers = modifyingCustomer.sub_customers || [modifyingCustomer];
                                const allReservationItems = subCustomers.flatMap(sc => (sc.reservations?.items || []).map(item => ({...item, source: 'reservation'})));
                                const allOrderItems = subCustomers.flatMap(sc => (sc.orders?.items || []).map(item => ({...item, source: 'order'})));
                                
                                return (
                                    <>
                                        {/* Current Products - Reservations */}
                                        {allReservationItems.length > 0 && (
                                            <div className="mb-6">
                                                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                                                    <Package size={16} className="text-amber-600" />
                                                    Produse din Rezervări
                                                </h3>
                                                <div className="space-y-3">
                                                    {allReservationItems.map((item, idx) => {
                                                        const modIndex = modifications.findIndex(m => m.item_name === item.name && m.action === 'remove');
                                                        const isSelected = modIndex !== -1;
                                                        const mod = isSelected ? modifications[modIndex] : null;
                                                        
                                                        return (
                                                            <div key={idx} className={`rounded-xl border overflow-hidden ${isSelected ? 'bg-red-50 border-red-300' : 'bg-zinc-50 border-zinc-200'}`}>
                                                                <div className="flex items-center justify-between p-3">
                                                                    <div>
                                                                        <span className="font-medium text-zinc-800">{item.name}</span>
                                                                        <span className="text-zinc-500 ml-2">x{item.quantity}</span>
                                                                        <span className="text-amber-600 ml-2">({formatPrice(item.total)} RON)</span>
                                                                    </div>
                                                                    {isSelected ? (
                                                                        <button
                                                                            onClick={() => removeModification(modIndex)}
                                                                            className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 bg-zinc-200 text-zinc-600 hover:bg-zinc-300"
                                                                        >
                                                                            <X size={14} />
                                                                            Anulează
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => addRemovalItem(item, 'reservation')}
                                                                            className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 bg-red-100 text-red-700 hover:bg-red-200"
                                                                        >
                                                                            <Minus size={14} />
                                                                            Elimină
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Reason selector - appears immediately when selected */}
                                                                {isSelected && (
                                                                    <div className="px-3 pb-3 pt-1 border-t border-red-200 bg-red-100/50">
                                                                        <label className="text-sm text-red-700 font-medium mb-1 block">De ce doriți să eliminați acest produs?</label>
                                                                        <select
                                                                            value={mod.reason}
                                                                            onChange={(e) => updateModificationReason(modIndex, e.target.value)}
                                                                            className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                                                                        >
                                                                            <option value="">-- Selectați motivul --</option>
                                                                            {REMOVAL_REASONS.map(r => (
                                                                                <option key={r.value} value={r.value}>{r.label}</option>
                                                                            ))}
                                                                        </select>
                                                                        
                                                                        {mod.reason === 'altele' && (
                                                                            <input
                                                                                type="text"
                                                                                value={mod.custom_reason}
                                                                                onChange={(e) => updateCustomReason(modIndex, e.target.value)}
                                                                                placeholder="Descrieți motivul..."
                                                                                className="w-full mt-2 px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Current Products - Orders */}
                                        {allOrderItems.length > 0 && (
                                            <div className="mb-6">
                                                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                                                    <CheckCircle size={16} className="text-emerald-600" />
                                                    Produse din Comandă
                                                </h3>
                                                <div className="space-y-3">
                                                    {allOrderItems.map((item, idx) => {
                                                        const modIndex = modifications.findIndex(m => m.item_name === item.name && m.action === 'remove');
                                                        const isSelected = modIndex !== -1;
                                                        const mod = isSelected ? modifications[modIndex] : null;
                                                        
                                                        return (
                                                            <div key={idx} className={`rounded-xl border overflow-hidden ${isSelected ? 'bg-red-50 border-red-300' : 'bg-zinc-50 border-zinc-200'}`}>
                                                                <div className="flex items-center justify-between p-3">
                                                                    <div>
                                                                        <span className="font-medium text-zinc-800">{item.name}</span>
                                                                        <span className="text-zinc-500 ml-2">x{item.quantity}</span>
                                                                        <span className="text-emerald-600 ml-2">({formatPrice(item.total)} RON)</span>
                                                                    </div>
                                                                    {isSelected ? (
                                                                        <button
                                                                            onClick={() => removeModification(modIndex)}
                                                                            className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 bg-zinc-200 text-zinc-600 hover:bg-zinc-300"
                                                                        >
                                                                            <X size={14} />
                                                                            Anulează
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => addRemovalItem(item, 'order')}
                                                                            className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 bg-red-100 text-red-700 hover:bg-red-200"
                                                                        >
                                                                            <Minus size={14} />
                                                                            Elimină
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Reason selector */}
                                                                {isSelected && (
                                                                    <div className="px-3 pb-3 pt-1 border-t border-red-200 bg-red-100/50">
                                                                        <label className="text-sm text-red-700 font-medium mb-1 block">De ce doriți să eliminați acest produs?</label>
                                                                        <select
                                                                            value={mod.reason}
                                                                            onChange={(e) => updateModificationReason(modIndex, e.target.value)}
                                                                            className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                                                                        >
                                                                            <option value="">-- Selectați motivul --</option>
                                                                            {REMOVAL_REASONS.map(r => (
                                                                                <option key={r.value} value={r.value}>{r.label}</option>
                                                                            ))}
                                                                        </select>
                                                                        
                                                                        {mod.reason === 'altele' && (
                                                                            <input
                                                                                type="text"
                                                                                value={mod.custom_reason}
                                                                                onChange={(e) => updateCustomReason(modIndex, e.target.value)}
                                                                                placeholder="Descrieți motivul..."
                                                                                className="w-full mt-2 px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                            {/* Added Products List - only show products to add, removals are shown inline */}
                            {modifications.filter(m => m.action === 'add').length > 0 && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                                        <Plus size={16} className="text-green-600" />
                                        Produse de Adăugat ({modifications.filter(m => m.action === 'add').length})
                                    </h3>
                                    <div className="space-y-2">
                                        {modifications.filter(m => m.action === 'add').map((mod, idx) => {
                                            const actualIdx = modifications.findIndex(m => m === mod);
                                            return (
                                                <div key={idx} className="flex items-center justify-between p-3 rounded-xl border bg-green-50 border-green-200">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-0.5 bg-green-200 text-green-700 text-xs font-medium rounded">ADAUGĂ</span>
                                                        <span className="font-medium text-zinc-800">{mod.item_name}</span>
                                                        <span className="text-zinc-500">x{mod.quantity}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => removeModification(actualIdx)}
                                                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Add New Product */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                                    <Plus size={16} className="text-green-600" />
                                    Adaugă Produs Nou
                                </h3>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={addProductName}
                                        onChange={(e) => setAddProductName(e.target.value)}
                                        placeholder="Numele produsului dorit..."
                                        className="flex-1 px-4 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                    <input
                                        type="number"
                                        value={addProductQty}
                                        onChange={(e) => setAddProductQty(parseInt(e.target.value) || 1)}
                                        min="1"
                                        className="w-20 px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-center"
                                    />
                                    <button
                                        onClick={addNewProduct}
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 flex items-center gap-1"
                                    >
                                        <Plus size={18} />
                                        Adaugă
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={submitModifications}
                                disabled={modifications.length === 0 || submittingModification}
                                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submittingModification ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <MessageSquare className="w-5 h-5" />
                                )}
                                Trimite Cererea de Modificare
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
